import type { Task } from '@medplum/fhirtypes'
import { useMedplum, useSubscription } from '@medplum/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { logError } from '@/utils/errorUtils'
import { tefcaOpenReviewQueryKey } from '@/hooks/useTefcaManualLink'
import type { TefcaOpenReview } from './TefcaMatchReviewModal'

const TEFCA_MATCH_REVIEW_TASK_CODE = 'https://behuman.health/process|tefca-match-review'
const MATCH_REVIEW_REASON_SYSTEM = 'https://behuman.health/match-review-reason'
const MATCH_REVIEW_MISSING_FIELD_SYSTEM = 'https://behuman.health/match-review-missing-field'

// Mirrors how the sync bot records the reason on the review Task
// (packages/middleware/src/super/bots/shared/hg-sync.ts)
function parseOpenReview(task: Task): TefcaOpenReview {
  return {
    reason:
      task.reasonCode?.coding?.find((coding) => coding.system === MATCH_REVIEW_REASON_SYSTEM)
        ?.code ?? 'no-match',
    missingFields: (task.input ?? [])
      .filter((input) =>
        input.type?.coding?.some((coding) => coding.system === MATCH_REVIEW_MISSING_FIELD_SYSTEM),
      )
      .map((input) => input.valueCode)
      .filter((code): code is string => Boolean(code)),
  }
}

/**
 * The patient's open TEFCA Match Review, if any: present while the automatic
 * TEFCA sync could not link the patient, null otherwise. Clears automatically
 * when the review Task completes (e.g. a later sync run finds an exact match).
 */
export function useTefcaOpenReview(patientId: string): TefcaOpenReview | null {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  const { data: openReview = null } = useQuery<TefcaOpenReview | null>({
    queryKey: tefcaOpenReviewQueryKey(patientId),
    retry: false,
    queryFn: async () => {
      try {
        const task = await medplum.searchOne(
          'Task',
          `code=${TEFCA_MATCH_REVIEW_TASK_CODE}&patient=Patient/${patientId}&status=requested`,
        )
        return task ? parseOpenReview(task) : null
      } catch (error) {
        logError('patient', 'Fetch TEFCA review status', error)
        return null
      }
    },
  })

  useSubscription(`Task?code=${TEFCA_MATCH_REVIEW_TASK_CODE}&patient=Patient/${patientId}`, () => {
    void queryClient.invalidateQueries({ queryKey: tefcaOpenReviewQueryKey(patientId) })
  })

  return openReview
}
